import styled from "styled-components"
import { getCssSizeValue, isStringEmpty } from "@/utils/utils"
import { BREAKPOINTS } from "@/styles/themeSettings"
import Image from "@/components/common/Image"
import { IMAGE_FALLBACK } from "@/constants"
import Video from "@/components/common/Video"
import React, { useMemo, useState } from "react"
import useDevice from "@/hooks/useDevice"
import VideoControls from "@/components/Banners/SubPage/Components/VideoControls"
import GridContainer from "@/components/common/GridContainer"
import BannerCard from "@/components/Banners/SubPage/Components/BannerCard"
import AssetWrapper from "@/components/Banners/SubPage/Components/AssetWrapper"
import useKey from "@/hooks/useKey"

const Wrapper = styled.section`
	overflow: hidden;
	position: relative;
	height: 100svh;
	width: 100%;
	display: flex;
	align-items: flex-end;
	padding-bottom: ${getCssSizeValue(20, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding-bottom: ${getCssSizeValue(48)};
	}
`

const CommonVideo = styled(Video)`
	border-radius: ${({ $isVideoPlaying }) => (!$isVideoPlaying ? "24px" : 0)};
	overflow: hidden;
	position: relative;
	width: 100%;
	height: 100%;
`

const StyledGridContainer = styled(GridContainer)`
	position: relative;
	z-index: 3;
	align-items: end;
`

const StyledVideoCover = styled.div`
	height: 100%;
	width: 100%;
	position: absolute;
	z-index: 2;

	transition: opacity 0.2s;

	img {
		border-radius: ${({ $isVideoPlaying }) => (!$isVideoPlaying ? "24px" : 0)};
	}

	&.hide {
		opacity: 0;
		pointer-events: none;
	}
`

const StyledDescription = styled.div`
	position: absolute;
	bottom: 0;
	right: 0;
	height: 50px;
	width: 0;
	background: ${({ theme }) => theme.colors.richBlack};
	color: ${({ theme }) => theme.colors.whiteSmoke};
	z-index: 10;
	border-bottom-right-radius: 24px;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	transition: all 0.3s;

	div {
		padding-right: ${getCssSizeValue(25)};
		transition: all 0.4s;
		translate: 100% 0;
		white-space: nowrap;
	}

	&.show {
		border-bottom-left-radius: 24px;
		width: 100%;

		div {
			translate: 0;
		}
	}
`

export default function ImageWithButtonToStartBackgroundVideo({ data }) {
	const assetVideoUrl = data?.video?.url || ""
	const externalVideoUrl = data?.externalVideo?.url || ""
	const videoSrc = assetVideoUrl !== "" ? assetVideoUrl : externalVideoUrl
	const videoDescription = data?.videoThumbnailDescription || ""

	const [isVideoPlaying, setIsVideoPlaying] = useState(false)
	const [isHovered, setIsHovered] = useState(false)
	const orientation = useDevice("orientation")

	const image = useMemo(() => {
		const image = orientation === "landscape" ? data?.image : data?.imageVertical
		return image || data?.image || IMAGE_FALLBACK //if vertical image is not set use default one or fallback
	}, [orientation, data])

	const videoCoverImage = data?.videoThumbnailCover || {}

	const handleToggleVideo = () => {
		setIsVideoPlaying((prev) => !prev)
	}

	const handleToggleHover = () => {
		setIsHovered((prev) => !prev)
	}

	useKey("Escape", () => setIsVideoPlaying(false))

	return (
		<Wrapper>
			<AssetWrapper>
				<Image
					src={image.url}
					alt={image.alt}
					priority
					placeholder={image.blurUpThumb}
					focalPoint={image.focalPoint}
					sizes="100vw"
				/>
			</AssetWrapper>

			<AssetWrapper
				className={`thumbnail ${isVideoPlaying ? "expanded" : ""}`}
				onMouseEnter={handleToggleHover}
				onMouseLeave={handleToggleHover}
				gridContainer
			>
				{!isStringEmpty(videoSrc) && (
					<VideoControls handleToggleVideo={handleToggleVideo} isVideoPlaying={isVideoPlaying} thumbnail hovered={isHovered} />
				)}
				<StyledVideoCover
					className={`${isVideoPlaying ? "hide" : ""} video-cover`}
					$isVideoPlaying={isVideoPlaying}
					onClick={handleToggleVideo}
				>
					<Image
						src={videoCoverImage?.url || ""}
						alt={videoCoverImage?.alt || ""}
						priority
						placeholder={image.blurUpThumb}
						focalPoint={image.focalPoint}
						sizes="100vw"
					/>
				</StyledVideoCover>
				<CommonVideo
					muted={false}
					$isVideoPlaying={isVideoPlaying}
					onClick={handleToggleVideo}
					src={videoSrc}
					playing={isVideoPlaying}
					resetOnPause={false}
				/>

				{!isVideoPlaying && (
					<StyledDescription className={`${isHovered ? "show" : ""} typo-strawford typo-strawford-50`}>
						<div>{videoDescription}</div>
					</StyledDescription>
				)}
			</AssetWrapper>

			<StyledGridContainer>
				<BannerCard data={data} />
			</StyledGridContainer>
		</Wrapper>
	)
}
